/*
 * Copyright (c) 2022 automata-network/tgb-donation authors and contributors.
 * This software may be modified and distributed under the terms of MIT license.
 * Refer to the LICENSE file for details.
 */
// import { useEffect } from 'react'
import { useEffect, useMemo, useState } from 'react'

import BigNumberJS from 'bignumber.js'
import styled, { css } from 'styled-components'

import CopyButton from 'src/components/CopyButton'
import { useModalContext } from 'src/contexts/ModalContext'
import { shortenAddress } from 'src/functions/address/shortenAddress'
import { calculateDuration } from 'src/functions/time'

// import { usePollContext } from '../../../contexts/PollProvider'
import { useThemeContext } from '../../../contexts/ThemeContext'
import { pxToEm, pxToVw } from '../../../functions/units'
import { VAPaymentMutationResponse } from '../../../types/api'
import FlexBox from '../../Common/FlexBox'
import Icon from '../../Icon'

export type Props = {
  expiredAt: string
  paymentAmount?: number
  transferInstructions?: VAPaymentMutationResponse['attributes']['payment_method']['instructions']
  txId: string
}

const DonateWire = ({ expiredAt, paymentAmount, transferInstructions, txId }: Props) => {
  const { colors } = useThemeContext()
  const { closeModal, modalIsOpen, openedModalStack } = useModalContext()

  const [timeLeft, setTimeLeft] = useState<
    | 0
    | {
        minutes: number
        seconds: number
      }
  >()

  const humanizedTimeLeft = useMemo(() => {
    if (timeLeft === undefined) return 'Loading...'
    if (timeLeft === 0) return 'Expired'

    const zeroPaddedSeconds = timeLeft.seconds < 10 ? `0${timeLeft.seconds}` : timeLeft.seconds
    return `${timeLeft.minutes}:${zeroPaddedSeconds}`
  }, [timeLeft])

  useEffect(() => {
    if (expiredAt && timeLeft !== 0) {
      const timer = setTimeout(() => {
        setTimeLeft(calculateDuration(new Date(expiredAt)))
        clearTimeout(timer)
      }, 1000)

      return () => {
        clearTimeout(timer)
      }
    } else if (timeLeft === 0 && modalIsOpen && openedModalStack.includes(txId)) {
      closeModal(txId)
    }
  }, [closeModal, expiredAt, modalIsOpen, openedModalStack, timeLeft, txId])

  if (!paymentAmount || !transferInstructions) {
    return null
  }

  return (
    <>
      <StyledArticle>
        <FlexBox as="header" columnGap={12} alignItems="center">
          <Icon icon="caret-right-circle" color={colors.primary600} size={26} />
          <h2>How to make this payment?</h2>
        </FlexBox>

        <ol>
          <li>Login to your ibanking page / app and go to the transfer tab.</li>
          <li>Add a new recipient by following the information displayed below.</li>
          <li>
            Once payment is successfuly received, this page will update automatically within 5 mins.{' '}
            <em>(You may need to refresh the page.)</em>
          </li>
        </ol>
      </StyledArticle>

      <StyledHorizontalLine />

      <>
        <StyledWireDetailRow justifyContent="space-between" alignItems="center">
          <span>Recipient Name:</span>
          <FlexBox alignItems="center" columnGap={9}>
            <em>XFers Pte Ltd</em>
            <CopyButton text="XFers Pte. Ltd." />
          </FlexBox>
        </StyledWireDetailRow>

        <StyledHorizontalLine />

        <StyledWireDetailRow justifyContent="space-between" alignItems="center">
          <span>Recipient Bank:</span>
          <FlexBox alignItems="center" columnGap={9}>
            <em>{transferInstructions.bank_short_code}</em>
            <CopyButton text={transferInstructions.bank_short_code} />
          </FlexBox>
        </StyledWireDetailRow>

        <StyledHorizontalLine />

        <StyledWireDetailRow justifyContent="space-between" alignItems="center">
          <span>Recipient Number:</span>
          <FlexBox alignItems="center" columnGap={9}>
            <em>{transferInstructions.account_no}</em>
            <CopyButton text={transferInstructions.account_no} />
          </FlexBox>
        </StyledWireDetailRow>

        <StyledHorizontalLine />

        <StyledWireDetailRow justifyContent="space-between" alignItems="center">
          <span>Amount being transfered:</span>
          <FlexBox alignItems="center" columnGap={9}>
            <em>
              {new BigNumberJS(paymentAmount).toFormat({
                decimalSeparator: '.',
                groupSeparator: ',',
                groupSize: 3,
                prefix: 'SGD $'
              })}
            </em>
            <CopyButton text={new BigNumberJS(paymentAmount).toString().replaceAll(',', '')} />
          </FlexBox>
        </StyledWireDetailRow>

        <StyledHorizontalLine />

        {process.env.REACT_APP_ENV === 'staging' && txId ? (
          <>
            <StyledWireDetailRow justifyContent="space-between" alignItems="center">
              <span>Transaction ID:</span>
              <FlexBox alignItems="center" columnGap={9}>
                <em>{shortenAddress(txId, 14, 5)}</em>
                <CopyButton text={txId} />
              </FlexBox>
            </StyledWireDetailRow>

            <StyledHorizontalLine />
          </>
        ) : null}

        <StyledWireDetailRow justifyContent="space-between" alignItems="center">
          <span>Payment expired at:</span>
          <FlexBox alignItems="center" columnGap={9}>
            <em>{humanizedTimeLeft}</em>
            <StyledRowIcon as="span">
              <Icon icon="timer" size={19} color={colors.gray600} />
            </StyledRowIcon>
          </FlexBox>
        </StyledWireDetailRow>
      </>
    </>
  )
}

export default DonateWire

const StyledArticle = styled.article`
  h2 {
    margin: 0;
    font-size: ${pxToVw(24)};
    font-weight: 500;
    color: ${({ theme }) => theme.colors.gray500};
  }

  p {
    margin: 0;
    margin-top: ${pxToVw(15)};
    font-size: ${pxToVw(18)};
    line-height: ${pxToEm(24, 18)};
    color: ${({ theme }) => theme.colors.gray};
  }

  ol {
    margin: 0;
    margin-top: ${pxToVw(15)};
    padding-left: ${pxToVw(24)};
    font-size: ${pxToVw(18)};
    line-height: ${pxToEm(24, 18)};
    color: ${({ theme }) => theme.colors.gray};

    > li:not(:last-child) {
      margin-bottom: ${pxToVw(6)};
    }
  }

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      h2 {
        font-size: ${pxToVw(24, 720)};
      }

      p {
        margin-top: ${pxToVw(15, 720)};
        font-size: ${pxToVw(18, 720)};
      }

      ol {
        margin-top: ${pxToVw(15, 720)};
        padding-left: ${pxToVw(24, 720)};
        font-size: ${pxToVw(18, 720)};

        > li:not(:last-child) {
          margin-bottom: ${pxToVw(6, 720)};
        }
      }
    `}
  `}
`

const StyledHorizontalLine = styled.hr`
  margin-block: 1.5625vw;
  border: none;
  border-top: solid 1px ${({ theme }) => theme.colors.gray200};

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      margin-block: ${pxToVw(30, 720)};
    `}
  `}
`

const StyledWireDetailRow = styled(FlexBox)`
  margin-block: ${pxToVw(-16)};

  > span {
    font-weight: 500;
    font-size: ${pxToVw(20)};
    color: ${({ theme }) => theme.colors.gray500};
  }

  em {
    font-size: ${pxToVw(20)};
    color: ${({ theme }) => theme.colors.gray};
  }

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      margin-block: ${pxToVw(-16, 720)};

      > span {
        font-size: ${pxToVw(20, 720)};
      }

      em {
        font-size: ${pxToVw(20, 720)};
      }
    `}
  `}
`

const StyledRowIcon = styled(CopyButton)``
